<template>
  <div class="text-gray-600">
    <div class="max-w-1500">
      <!-- Hero -->
      <div class="w-full flex justify-center relative overflow-hidden">
        <OImage class="w-full" v-bind="getImagePath('hero')" img-class="w-full" />
        <div class="max-w-1200 absolute ml-7 left-0 right-0 top-0 bottom-0 flex justify-center">
          <div class="hidden md:block absolute hero-overlay-text text-white">
            <h1 class="landingpage-hero-title text-gray-600 landingpage-hero-title-desktop o-heading-2 font-extrabold" v-text="getTranslation('Title')" />
            <p class="landingpage-hero-description text-gray-600 landingpage-hero-description-desktop o-body mb-7 mt-rem" v-text="getTranslation('Description')" />
            <div class="text-left hidden md:block">
              <NewButton @click="onButtonClick">
                {{ shouldLogin ? $t('LoginForm.Login') : getTranslation('CTA') }}
              </NewButton>
            </div>
          </div>
        </div>
      </div>
      <div class="md:hidden text-neutral-00 px-7 py-12" :style="{ backgroundColor: primaryColor }">
        <h1 class="landingpage-hero-title landingpage-hero-title-mobile o-heading-2 text-gray-600" v-text="getTranslation('Title')" />
        <p class="landingpage-hero-description landingpage-hero-description-mobile o-body mb-7 mt-rem text-gray-600" v-text="getTranslation('Description')" />
        <div class="text-left">
          <NewButton @click="onButtonClick">
            {{ shouldLogin ? $t('LoginForm.Login') : getTranslation('CTA') }}
          </NewButton>
        </div>
      </div>

      <!-- First Block UL -->
      <div class="max-w-1200 py-16 gap-5 md:grid grid-cols-2 md:py-24">
        <div class="">
          <div class="">
            <ul class="pt-3 item-list">
              <li v-for="(item, index) in items" :key="index" class="flex mb-5 items-center">
                <div>
                  <div class="p-2 flex-grow-0 flex items-center justify-center mr-5 rounded-full bg-newGreen">
                    <OSvg class="fill-neutral-00 w-5 h-5" svg="checkmark" />
                  </div>
                </div>
                <span class="o-body font-bold font-display text-xl" v-text="item" />
              </li>
            </ul>
          </div>
        </div>
        <div class="mt-24 md:mt-0">
          <div class="ml-auto">
            <h3 class="o-heading-2 mb-rem text-left md:mt-3" v-text="getTranslation('FirstBlock.Title')" />
            <p class="o-body text-left" v-text="getTranslation('FirstBlock.Description')" />
            <div class="text-left pt-7">
              <NewButton @click="onButtonClick">
                {{ shouldLogin ? $t('LoginForm.Login') : getTranslation('CTA') }}
              </NewButton>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Form -->
    <div v-loading="loading" class="bg-gray-600 relative text-neutral-00">
      <div
        class="relative max-w-1200 pb-24 pt-12 md:pt-24 md:flex"
      >
        <div class="pr-7 flex-1 pb-12">
          <div class="max-w-box">
            <h3 class="mb-3 pb-rem ota-headline" v-text="getTranslation('FormBlock.Title')" />
            <ul class="mb-7">
              <li v-for="(step, number) in stepsToShow" :key="step" class="mb-7">
                <div class="flex">
                  <div class="flex-shrink-0 w-7 h-7 rounded-full font-display text-xl flex justify-center items-center text-green bg-yellow-400 mr-3">
                    <span class="font-body text-xl font-bold text-gray-600" v-text="number + 1" />
                  </div>
                  <span class="o-body font-bold font-display text-xl" v-text="step" />
                </div>
              </li>
            </ul>
            <p v-if="isAddMembershipLandingPage" class="o-body" v-text="getTranslation('FormBlock.Explanation')" />
          </div>
        </div>
        <div class="flex-1 px-0 md:pl-7">
          <client-only>
            <div ref="form" :class="['bg-gray-50 memberships-container text-gray-600 rounded-lg pb-5 pt-10 relative']">
              <div v-if="isReactivateLandingPage" :class="[(paymentContractIsInactive || !members.length || hasOpenPayments) ? 'pointer-events-none opacity-25' : '']">
                <h3 class="o-heading-2 px-10 mb-5" v-text="getTranslation('WhichMembership')" />
                <div class="px-4">
                  <div
                    v-for="membership in members"
                    :key="membership.memberId"
                    :class="['px-5 pt-4 bg-neutral-00 border rounded-lg mb-rem', activeMemberIds.includes(membership.memberId) ? 'border-green-500' : 'border-gray-300']"
                  >
                    <p class="o-heading-4 font-bold mb-2" v-text="membership.memberName" />
                    <p
                      v-for="(pgs, i) in membership.productGroupSizes"
                      :key="`${i}-${pgs.productGroupSizeReference}`"
                      class="o-body pb-4"
                      v-text="`${pgs.productGroup} (${pgs.productGroupSizeLabel})`"
                    />
                    <div class="flex items-center relative pb-5">
                      <input
                        :id="`membership-${membership.memberId}`"
                        v-model="activeMemberIds"
                        :value="membership.memberId"
                        type="checkbox"
                        :class="`w-9 h-9 rounded-md appearance-none border-2 border-gray-300 checked:bg-green-400 checked:border-green-400 focus:outline-none cursor-pointer transition-colors duration-200 ease-in-out ${activeMemberIds.includes(membership.memberId) ? 'bg-green-300 border-green-400' : 'bg-neutral-00 border-gray-300'}`"
                      >
                      <OSvg class="fill-neutral-00 w-5 h-5 absolute absolute-checkmark" svg="checkmark" />
                      <label :for="`membership-${membership.memberId}`" :class="`o-body ml-4 font-bold cursor-pointer ${activeMemberIds.includes(membership.memberId) ? 'text-green-300' : 'text-gray-600'}`">
                        {{ getTranslation('Activate') }}
                      </label>
                    </div>
                  </div>
                  <div class="flex justify-center pt-5">
                    <NewButton :disabled="!activeMemberIds.length" color="green" class="w-full" @click="reactivateMemberships">
                      {{ getTranslation('CTA') }}
                    </NewButton>
                  </div>
                </div>
              </div>
              <div v-else-if="isAddMembershipLandingPage" :class="[(paymentContractIsInactive || hasOpenPayments) ? 'pointer-events-none opacity-25' : '']">
                <div
                  ref="signupForm"
                >
                  <div
                    v-loading="loading"
                    class="max-w-success rounded-lg bg-gray-50 relative text-gray-600"
                  >
                    <template v-if="!success">
                      <div class="text-left">
                        <h4 class="o-heading-2 px-12 pb-5" v-text="getTranslation('FormBlock.YourDetails')" />
                      </div>
                      <div
                        class="bg-gray-100 flex py-5 px-12 mb-5 lg:block relative"
                      >
                        <div v-if="!isEditing">
                          <h3 class="ota-body mb-3" v-text="`${form.firstName} ${form.lastName}`" />
                          <AddressFormat
                            :address="displayAddress"
                            :country-code="country"
                          />
                        </div>
                        <div
                          v-else
                        >
                          <CreateAccountForm
                            ref="createAccountForm"
                            :errors="errors"
                            :first-name="form.firstName"
                            :last-name="form.lastName"
                            :country="country"
                            minimal
                            @change="onNameChange"
                          />
                          <AddressForm
                            ref="addressForm"
                            hide-submit
                            :address="form"
                            :country-code="country"
                            :errors="errors"
                            @change="onAddressChange"
                          />
                        </div>
                        <button
                          class="edit-button focus:outline-none hidden"
                          :aria-label="isEditing ? $t('Accessibilty.AriaLabels.StopEditingInfo') : $t('Accessibilty.AriaLabels.StartEditingInfo')"
                          @click="isEditing = !isEditing"
                        >
                          <OSvg svg="black-edit" />
                        </button>
                      </div>
                      <div class="socks-size-select mb-1 px-12">
                        <OSelect
                          class="mx-auto inline-block"
                          :label="$t('Size')"
                          :clearable="false"
                          :value="chosenSize"
                          :options="sizeOptions"
                          @input="chosenSize = $event"
                        />
                      </div>
                      <div class="pb-rem px-12">
                        <p class="ota-p12 mb-5" v-text="getTranslation('FormBlock.ExtraInfo')" />
                        <NewButton
                          @click="onSubmit"
                        >
                          {{ getTranslation('FormBlock.CTA') }}
                        </NewButton>
                      </div>
                      <h5 class="ota-p12 text-left pb-7 px-12" v-text="getTranslation('FormBlock.Notice', { TermsAndConditions: getTranslation('FormBlock.TermsAndConditions'), TermsOfUse: getTranslation('FormBlock.TermsOfUse'), PrivacyStatement: getTranslation('FormBlock.PrivacyStatement') })" />
                    </template>
                    <div v-else class="relative">
                      <div class="text-left">
                        <h4 class="ota-title pt-7 pl-12 pb-5" v-text="getTranslation('FormBlock.Success')" />
                      </div>
                      <p class="o-body text-left pb-7 px-10" v-html="getTranslation('FormBlock.SuccessMessage')" />
                      <div
                        class="bg-gray-100 py-5 px-12 mb-5 relative pb-10 rounded-b-lg"
                      >
                        <div>
                          <h3 class="ota-body mb-3" v-text="`${form.firstName} ${form.lastName}`" />
                          <AddressFormat
                            :address="displayAddress"
                            :country-code="country"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="paymentContractIsInactive" class="absolute top-0 left-0 w-full h-full px-10 flex justify-center items-center">
                <div class="bg-neutral-00 rounded-lg p-10">
                  <p class="o-heading-2 mb-5" v-text="getTranslation('PaymentContract.Title')" />
                  <p class="o-body" v-text="getTranslation('PaymentContract.Description')" />

                  <div class="flex justify-center pt-5">
                    <NewButton tag="nuxt-link" :to="`/account/personal-details/payment-details?successUrl=${updatePaymentSuccessUrl}`">
                      {{ getTranslation('PaymentContract.CTA') }}
                    </NewButton>
                  </div>
                </div>
              </div>
              <div v-else-if="hasOpenPayments" class="absolute top-0 left-0 w-full h-full px-10 flex justify-center items-center">
                <div class="bg-neutral-00 rounded-lg p-10">
                  <p class="o-heading-2 mb-5" v-text="getTranslation('OpenPayment.Title')" />
                  <p class="o-body" v-text="getTranslation('OpenPayment.Description')" />
                  <div class="flex justify-center pt-5">
                    <NewButton tag="a" :href="paymentLink">
                      {{ getTranslation('OpenPayment.CTA') }}
                    </NewButton>
                  </div>
                </div>
              </div>
              <div v-else-if="isReactivateLandingPage && !members.length && !shouldLogin" class="absolute top-0 left-0 w-full h-full px-10 flex justify-center items-center">
                <div class="bg-neutral-00 rounded-lg p-10">
                  <p class="o-heading-2 mb-5" v-text="getTranslation('NoMemberships.Title')" />
                  <p class="o-body" v-text="getTranslation('NoMemberships.Description')" />
                </div>
              </div>
              <div v-else-if="shouldLogin" class="absolute top-0 left-0 w-full h-full px-10 flex justify-center items-center">
                <div class="bg-neutral-00 rounded-lg p-10">
                  <p class="o-heading-2 mb-5" v-text="getTranslation('ShouldLogin.Title')" />
                  <p class="o-body" v-text="getTranslation('ShouldLogin.Description')" />
                  <div class="flex justify-center pt-5">
                    <NewButton @click="redirectToLogin">
                      {{ $t('LoginForm.Login') }}
                    </NewButton>
                  </div>
                </div>
              </div>
            </div>
          </client-only>
        </div>
      </div>
    </div>

    <div class="bg-neutral-00 relative py-12 md:py-24">
      <div class="md:flex max-w-1200">
        <div class="flex-1 pr-7">
          <h3 class="o-heading-1" v-text="getTranslation('FAQ.Title')" />
        </div>
        <div class="flex-1 pt-12 md:pt-2">
          <Faq :items="faqItems" />
        </div>
      </div>
    </div>

    <!-- Sticky ATC -->
    <StickyATC class="md:hidden" :text="shouldLogin ? $t('LoginForm.Login') : getTranslation('CTA')" @click="onButtonClick" />

    <SuccessModal
      ref="successModal"
      :title="getTranslation('Modal.Title')"
      :text="getTranslation('Modal.Text')"
      @closed="onClosed"
    />
  </div>
</template>

<script>
import { CountryCodes, Currencies, Locales } from '@/enums/general'
import { addLocalCurrency, capitalize, getImagePaths, serializeParameters } from '@/utils/general'
import OImage from '@/components/OImage'
import OSvg from '@/components/OSvg'
import NewButton from '@/components/NewButton'
import SuccessModal from '@/components/Modals/SuccessModal'
import Faq from '@/components/Faq'
import {
  NEW_GET_PAYMENT_CONTRACT,
  GET_REACTIVATABLE_MEMBERSHIPS,
  REACTIVATE_REACTIVATABLE_MEMBERSHIPS,
  NEW_GET_OPEN_PAYMENTS,
  CREATE_MEMBER_ON_LANDINGPAGE
} from '@/utils/store/actionTypes'
import OSelect from '@/components/OSelect'
import { mapActions, mapGetters } from 'vuex'
import { LastOrderCreationDates } from '@/enums/order'
import StickyATC from '@/components/StickyATC'
import { LandingPageType } from '@/enums/landing'
import CreateAccountForm from '@/components/Forms/CreateAccountForm'
import AddressForm from '@/components/Forms/AddressForm'
import AddressFormat from '@/components/AddressFormat'
import {
  NewOrientations,
  SocksDiscountPrices,
  SocksPrices,
  UndershirtsDiscountPrices,
  UndershirtsPrices
} from '@/enums/products'
import { idPrefixes } from '@/enums/signup'
import { pushToDataLayer } from '@/utils/gtm'
import { identifyUserEvent } from '@/utils/account'
import { getQueryParam } from '@/utils/urls'
import { GET_COUNTRY_DATA } from '@/utils/store/getterTypes'

export default {
  name: 'LandingPageBody',
  components: {
    OImage,
    OSvg,
    NewButton,
    StickyATC,
    Faq,
    SuccessModal,
    CreateAccountForm,
    AddressForm,
    AddressFormat,
    OSelect
  },
  props: {
    locizePath: {
      type: String,
      default: ''
    },
    primaryColor: {
      type: String,
      default: '#004B6B'
    },
    imagePath: {
      type: String,
      default: ''
    },
    appCountry: {
      type: String,
      default: CountryCodes.netherlands
    },
    locale: {
      type: String,
      default: Locales.nl
    },
    userIdentifier: {
      type: Number,
      default: null
    },
    landingpageUrl: {
      type: String,
      default: '/reactivate'
    },
    type: {
      type: String,
      default: LandingPageType.reactivate
    },
    authUser: {
      type: Object,
      default: null
    },
    getSizeOptions: {
      type: Function,
      default: () => ([])
    },
    membershipTypeReference: {
      type: String,
      default: ''
    },
    orientation: {
      type: String,
      default: 'men'
    }
  },
  data () {
    return {
      loading: true,
      success: false,
      isEditing: true,
      shouldLogin: false,
      hashUserIdentifier: '',
      campaignId: 0,
      reactivatableHash: '',
      openPaymentsHash: '',
      paymentContractHash: '',
      members: [],
      activeMemberIds: [],
      paymentContract: null,
      openPayments: [],
      chosenSize: null,
      errors: {},
      form: {
        userId: '',
        firstName: '',
        lastName: '',
        email: '',
        annex: '',
        area: '',
        country: '',
        building: '',
        city: '',
        floor: '',
        hash: '',
        houseNumber: '',
        postalCode: '',
        region: '',
        street: '',
        streetType: '',
        countryCode: ''
      }
    }
  },
  computed: {
    country () {
      return this.form.country || this.appCountry
    },
    nextMonth () { // TODO: 1 waarheid fix (https://onthatass.atlassian.net/browse/IT-7763)
      return capitalize(this.$moment().add(1, 'months').format('MMMM'))
    },
    sizeOptions () {
      return this.getSizeOptions({ country: this.country })
    },
    isAddMembershipLandingPage () {
      return this.type === LandingPageType.addMembership
    },
    isReactivateLandingPage () {
      return this.type === LandingPageType.reactivate
    },
    userId () {
      return this.userIdentifier || this.hashUserIdentifier
    },
    successUrl () {
      if (this.isAddMembershipLandingPage) {
        const {
          userId,
          open_payments_hash: openPaymentsHash,
          payment_contract_details_hash: paymentContractHash,
          campaign_hash: campaignHash,
          genericHash: hasGenericHash // Dont use this one because it's already encoded improperly
        } = this.$route.query

        if (hasGenericHash) {
          const rawGenericHash = getQueryParam(this.$route.fullPath, 'genericHash')
          return encodeURIComponent(`${process.env.BASE_URL}${this.landingpageUrl}?action=open-payment-paid&userId=${userId}&campaign_hash=${encodeURIComponent(campaignHash)}&open-payment-paid=true&genericHash=${rawGenericHash}`)
        }

        return encodeURIComponent(`${process.env.BASE_URL}${this.landingpageUrl}?action=open-payment-paid&userId=${userId}&open_payments_hash=${encodeURIComponent(openPaymentsHash)}&payment_contract_details_hash=${encodeURIComponent(paymentContractHash)}&campaign_hash=${encodeURIComponent(campaignHash)}&open-payment-paid=true`)
      }

      const {
        userId,
        open_payments_hash: openPaymentsHash,
        payment_contract_details_hash: paymentContractHash,
        reactivateable_members_hash: reactivatableHash,
        genericHash: hasGenericHash // Dont use this one because it's already encoded improperly
      } = this.$route.query

      if (hasGenericHash) {
        const rawGenericHash = getQueryParam(this.$route.fullPath, 'genericHash')
        return encodeURIComponent(`${process.env.BASE_URL}${this.landingpageUrl}?action=open-payment-paid&userId=${userId}&open-payment-paid=true&genericHash=${rawGenericHash}`)
      }

      return encodeURIComponent(`${process.env.BASE_URL}${this.landingpageUrl}?action=open-payment-paid&userId=${userId}&open_payments_hash=${encodeURIComponent(openPaymentsHash)}&payment_contract_details_hash=${encodeURIComponent(paymentContractHash)}&reactivateable_members_hash=${encodeURIComponent(reactivatableHash)}&open-payment-paid=true`)
    },
    updatePaymentSuccessUrl () {
      return encodeURIComponent(`${process.env.BASE_URL}${this.landingpageUrl}?action=added-payment-method`)
    },
    paymentLink () {
      if (this.openPayments.length) {
        return `${this.openPayments[0].paymentLink}&successUrl=${this.successUrl}`
      }

      return null
    },
    variables () {
      const countryCode = `${this.locale}-${this.country}`

      return {
        loyaltyAmount: addLocalCurrency(10, countryCode, Currencies.nl),
        twentyAmount: addLocalCurrency(20, countryCode, Currencies.nl),
        undershirtsPrice: addLocalCurrency(UndershirtsPrices[this.country], countryCode, Currencies[this.country]),
        undershirtsDiscountPrice: addLocalCurrency(UndershirtsDiscountPrices[this.country], countryCode, Currencies[this.country]),
        socksPrice: addLocalCurrency(SocksPrices[this.country], countryCode, Currencies[this.country]),
        socksDiscountPrice: addLocalCurrency(SocksDiscountPrices[this.country], countryCode, Currencies[this.country]),
        firstDeliveryMonth: this.firstDeliveryMonth,
        newestDesign: this.$t(`${this.locizePath}.NewestDesign`),
        lastOrderCreationDate: LastOrderCreationDates[this.country],
        nextMonth: this.nextMonth
      }
    },
    hasOpenPayments () {
      return this.openPayments.length > 0 && !this.cameBackFromPayingOpenPayment
    },
    firstDeliveryMonth () {
      if (!this.countryData) {
        return ''
      }

      return this.$t(`Months[${this.$moment.utc(this.countryData.activationProjection.nextDeliveryDate).month()}]`)
    },
    paymentContractIsInactive () {
      return this.paymentContract?.status === 'inactive'
    },
    displayStreet () {
      return this.form.street ? this.form.street.replace(/,/g, ', ') : ''
    },
    displayAddress () {
      const address = { ...this.form, country: this.$t(`Countries.${this.country}`) }

      if (!this.isEditing) {
        return {
          ...address,
          street: this.displayStreet
        }
      }

      return address
    },
    items () {
      const path = `${this.locizePath}.FirstBlock.Items`
      const items = this.$t(path)

      if (!Array.isArray(items)) {
        return []
      }

      return items.map((_, i) => this.getTranslation(`FirstBlock.Items[${i}]`))
    },
    steps () {
      const path = `${this.locizePath}.FormBlock.Steps`
      const items = this.$t(path)

      if (!Array.isArray(items)) {
        return []
      }

      return items.map((_, i) => this.getTranslation(`FormBlock.Steps[${i}]`))
    },
    faqItems () {
      const path = `${this.locizePath}.FAQ.Items`
      const items = this.$t(path)

      if (!Array.isArray(items)) {
        return []
      }

      return items.map((_, index) => ({
        title: this.getTranslation(`FAQ.Items[${index}].Title`),
        description: this.getTranslation(`FAQ.Items[${index}].Description`)
      }))
    },
    isAuthenticated () {
      return !!this.userId
    },
    loggedInSteps () {
      const path = `${this.locizePath}.FormBlock.LoggedInSteps`
      const items = this.$t(path)

      if (!Array.isArray(items)) {
        return []
      }

      return items.map((_, i) => this.getTranslation(`FormBlock.LoggedInSteps[${i}]`))
    },
    stepsToShow () {
      return this.isAuthenticated ? this.loggedInSteps : this.steps
    },
    cameBackFromPayingOpenPayment () {
      return !!this.$route.query['open-payment-paid']
    },
    ...mapGetters('app', {
      countryData: GET_COUNTRY_DATA
    })
  },
  watch: {
    sizeOptions: {
      handler (newSizeOptions) {
        if (newSizeOptions && newSizeOptions.length) {
          this.chosenSize = newSizeOptions[0]
        }
      },
      deep: true
    }
  },
  async created () {
    if (process.client) {
      const {
        userId,
        open_payments_hash: openPaymentsHash,
        payment_contract_details_hash: paymentContractHash,
        reactivateable_members_hash: reactivatableHash,
        genericHash: hasGenericHash, // Dont use this one because it's already encoded improperly

        // Add membership data
        country,
        campaign_hash: campaignHash
      } = this.$route.query

      let rawGenericHash = ''

      if (hasGenericHash) {
        rawGenericHash = getQueryParam(this.$route.fullPath, 'genericHash')
      }

      if (userId) {
        this.hashUserIdentifier = userId
      }

      if (openPaymentsHash || hasGenericHash) {
        this.openPaymentsHash = hasGenericHash ? rawGenericHash : encodeURIComponent(openPaymentsHash)
      }

      if (paymentContractHash || hasGenericHash) {
        this.paymentContractHash = hasGenericHash ? rawGenericHash : encodeURIComponent(paymentContractHash)
      }

      if (reactivatableHash || hasGenericHash) {
        this.reactivatableHash = hasGenericHash ? rawGenericHash : encodeURIComponent(reactivatableHash)
      }

      if (this.isAddMembershipLandingPage) {
        if (this.authUser) {
          this.form.firstName = this.authUser.firstName
          this.form.lastName = this.authUser.lastName
          this.form.email = this.authUser.email
          this.form.country = this.authUser.user.billingAddress.country.name.toLowerCase()
          this.form.city = this.authUser.user.billingAddress.city
          this.form.postalCode = this.authUser.user.billingAddress.postalCode
          this.form.countryCode = this.authUser.user.billingAddress.country.name.toLowerCase() || CountryCodes.netherlands

          this.form.street = this.authUser.user.billingAddress.fullStreet
        } else {
          const countryCode = country ? country.toLowerCase() : CountryCodes.netherlands

          this.form.firstName = ''
          this.form.lastName = ''
          this.form.email = ''
          this.form.country = countryCode
          this.form.city = ''
          this.form.postalCode = ''
          this.form.countryCode = countryCode
          this.form.hash = campaignHash
          this.form.street = ''
        }
      }

      if (this.userId) {
        this.loading = true

        identifyUserEvent({ otaUserId: `${this.userId}` })

        await Promise.allSettled([
          this.isAddMembershipLandingPage ? null : this.fetchMembers({ userId: this.userId, _hash: this.reactivatableHash }),
          this.fetchPaymentContract({ userId: this.userId, _hash: this.paymentContractHash }),
          this.fetchOpenPayments({ userId: this.userId, _hash: this.openPaymentsHash })
        ])

        this.loading = false
      } else {
        this.shouldLogin = true
        this.loading = false
      }
    }
  },
  mounted () {
    const action = this.$route.query.action

    if (action === 'open-payment-paid') {
      this.$otoaster.success(this.$t('OpenPaymentPage.Success.Title'))
    } else if (action === 'added-payment-method') {
      this.$otoaster.success(this.$t('PersonalDetails.PaymentMethodeUpdated'))
    }
  },
  methods: {
    onNameChange ({ value, field }) {
      this.form[field] = value
    },
    onAddressChange ({ field, address }) {
      this.form[field] = address[field]
    },
    getTranslation (key) {
      return this.$t(`${this.locizePath}.${key}`, this.variables)
    },
    async onClosed () {
      this.loading = true

      await Promise.all([
        this.fetchMembers({ userId: this.userId, _hash: this.reactivatableHash }),
        this.fetchPaymentContract({ userId: this.userId, _hash: this.paymentContractHash }),
        this.fetchOpenPayments({ userId: this.userId, _hash: this.openPaymentsHash })
      ])

      this.loading = false
    },
    async onSubmit () {
      this.loading = true

      if (this.isEditing) {
        this.$refs.createAccountForm.$v.$touch()
        this.$refs.addressForm.$v.$touch()

        if (this.$refs.addressForm.$v.$invalid || this.$refs.createAccountForm.$v.$invalid) {
          if (this.$refs.addressForm.$v.$invalid) {
            this.$refs.addressForm.invalidForm = true
          }

          if (this.$refs.createAccountForm.$v.$invalid) {
            this.$refs.createAccountForm.invalidForm = true
          }

          this.$otoaster.danger(this.$t('Form.Error.RequiredFields'))

          this.loading = false
          return
        }
      }

      try {
        const variables = {
          user: `${idPrefixes.user}${this.userId}`,
          firstName: this.form.firstName,
          lastName: this.form.lastName,
          productGroupSize: `/api/product_group_sizes/reference/${this.chosenSize?.value}`,
          differentDeliveryAddress: this.isEditing,
          membershipTypeReference: this.membershipTypeReference,
          userValidationHash: this.form.hash,
          address: {
            houseNumberAddition: this.form.annex,
            area: this.form.area,
            building: this.form.building,
            city: this.form.city,
            floor: this.form.floor,
            houseNumber: this.form.houseNumber,
            postalCode: this.form.postalCode,
            region: this.form.region,
            street: this.form.street,
            streetType: this.form.streetType
          }
        }

        await this.createMember(variables)

        this.onSuccess()
        this.success = true
      } catch (error) {
        const errors = this.$ota.general.getCustomErrorMessages(error)

        if (errors.messages && Object.keys(errors.messages).length) {
          const formattedErrors = Object.keys(errors.messages).reduce((acc, key) => ({
            ...acc,
            ...(key.includes('address.') ? {
              [key.replace('address.', '')]: errors.messages[key]
            } : {
              [key]: errors.messages[key]
            })
          }), {})

          this.errors = formattedErrors
        } else {
          this.$ota.general.logError(error)
          const graphQLError = error.graphQLErrors?.find(error => error.hasOwnProperty('message'))?.message
          this.$otoaster.danger(graphQLError || this.$t('Socks.LandingsPage.ErrorDescription'))
        }
      }

      this.loading = false
    },
    scrollToForm () {
      try {
        this.$refs?.form.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        })
      } catch (error) {}
    },
    getImagePath (key) {
      return getImagePaths({
        s3: true,
        path: `${this.imagePath}${key}`
      })
    },
    onSuccess ({ payload } = {}) {
      let data = null

      if (payload) {
        data = btoa(encodeURIComponent(JSON.stringify(payload)))
      }

      const params = {
        userId: this.$route.query.userId,
        hash: this.$route.query.genericHash,
        action: 'hasReactivated',
        data
      }

      this.$router.push(`/explore-memberships?${serializeParameters(params)}`)
    },
    onButtonClick () {
      if (this.shouldLogin) {
        this.redirectToLogin()
      } else {
        this.scrollToForm()
      }
    },
    redirectToLogin () {
      this.$router.push(`/login?next=${this.landingpageUrl}`)
    },
    async fetchMembers ({ userId, _hash }) {
      try {
        const members = await this.getReactivatableMemberships({ userId, _hash })
        if (members.length && this.hasCampaignWithRewardForCurrentOrientation(members)) {
          this.members = members
          this.campaignId = members[0].activeCampaigns[0].id
        } else {
          this.members = []
        }
      } catch (error) {
        this.$otoaster.danger(this.$t('Error.general'))
      }
    },
    hasCampaignWithRewardForCurrentOrientation (members) {
      const orientations = this.orientation === 'men' ? [NewOrientations.men, NewOrientations.addon, NewOrientations.boys] : [this.orientation]

      const memberOfOrientation = members.find(member => orientations.includes(member.membershipType.orientation))
      if (!memberOfOrientation || memberOfOrientation.activeCampaigns.length === 0) {
        return false
      }

      const campaign = memberOfOrientation.activeCampaigns[0]

      return campaign.strategy !== 'metrics_only'
    },

    async fetchPaymentContract ({ userId, _hash }) {
      try {
        const paymentContract = await this.getPaymentContract({ userId, _hash })
        this.paymentContract = paymentContract
      } catch (error) {
        this.$otoaster.danger(this.$t('Error.general'))
      }
    },
    async fetchOpenPayments ({ userId, _hash }) {
      try {
        const openPayments = await this.getOpenPayments({ userId, _hash })
        this.openPayments = openPayments
      } catch (error) {
        this.$otoaster.danger(this.$t('Error.general'))
      }
    },
    async reactivateMemberships () {
      this.loading = true
      try {
        const payload = await this.reactivateReactivatableMemberships({ userId: this.userId, _hash: this.reactivatableHash, campaignId: this.campaignId, memberIds: this.activeMemberIds })

        this.activeMemberIds.forEach((memberId) => {
          pushToDataLayer({
            event: 'reactivatedByCampaign',
            userId: this.userId,
            memberId
          })
        })

        this.onSuccess({ payload: payload.reactivatedMembers[0] })
      } catch (error) {
        this.$otoaster.danger(this.$t('Error.general'))
      }
      this.loading = false
    },
    ...mapActions('user', {
      getPaymentContract: NEW_GET_PAYMENT_CONTRACT,
      getReactivatableMemberships: GET_REACTIVATABLE_MEMBERSHIPS,
      reactivateReactivatableMemberships: REACTIVATE_REACTIVATABLE_MEMBERSHIPS
    }),
    ...mapActions('payment', {
      getOpenPayments: NEW_GET_OPEN_PAYMENTS
    }),
    ...mapActions('member', {
      createMember: CREATE_MEMBER_ON_LANDINGPAGE
    })
  }
}
</script>

<style scoped lang="postcss">
.max-w-1500 {
  @apply mx-auto;
  max-width: 150rem;
}

.max-w-1200 {
  @apply mx-auto px-7;
  max-width: 120rem;
}

.max-w-box {
  max-width: 45rem;
}

.measure-button {
  >>> svg {
    @apply fill-gray-600 w-6 pt-0 !important;
  }

  >>> path {
    @apply fill-gray-600;
  }
}

.edit-button {
  @apply absolute cursor-pointer;
  right: 2.5rem;
  top: 2rem;
}

.oselect-wrapper >>> .v-select .vs__dropdown-toggle {
  min-width: 20rem;
  max-width: 100%;
}

.landingpage-hero-title-desktop {
  line-height: 4rem;
}

.max-w-500 {
  @apply mx-auto;
  max-width: 50rem;
}

.absolute-checkmark {
  @apply pointer-events-none w-5 h-5;
  left: 6px;
}

>>> .faq {
  @apply border-l-0 border-r-0 border-b-0 border-t-1 border-gray-600 rounded-none mb-7 !important;

  &-title {
    @apply font-body text-2xl font-normal;
    line-height: 3rem;
  }

  &-body {
    @apply px-0;
  }

  &-description {
    @apply font-body font-normal pl-0;
    font-size: 1.6rem;
    line-height: 2.8rem;
  }
}

.custom-size-p {
  font-size: 1.7rem !important;
}

.memberships-container {
  @apply mx-auto;
  max-width: 50rem;
}

.new-o-button-solid.new-o-button-green {
  @apply bg-green-500;
}

.hero-overlay-text {
  @apply mx-auto left-0 right-0 bottom-0 px-2;
  max-width: 33rem;
}

@screen xsplus {
  .hero-overlay-text {
    @apply pb-12;
  }
}

@screen container {
  .hero-overlay-text {
    @apply pb-20;
  }
}

.item-list {
  @apply mx-auto;
  max-width: 40rem;
}

.o-body.font-display {
  @apply font-display !important;
}

.o-heading-2.font-extrabold {
  @apply font-extrabold !important;
}

.w-4 {
  min-width: 1.2rem;
}

.bg-newGreen {
  background-color: #34A853;
}

.text-newGreen {
  color: #7FFF00;
}

@screen md {
  .custom-size-p {
    font-size: 2.5rem !important;
  }

  .landingpage-hero-title-desktop {
    line-height: 5rem;
  }

  .o-body.text-xl {
    @apply text-xl !important;
  }

  .item-list {
    margin-left: unset;
  }

  .memberships-container {
    margin-right: unset;
  }

  .hero-overlay-text {
    @apply pt-20 top-0 my-auto flex justify-center flex-col pl-7;
    max-width: 49rem;
    right: unset;

    h1 {
      font-size: 5rem;
    }
  }
}

@screen xl {
  .max-w-1200 {
    @apply px-0;
  }

  .hero-overlay-text {
    @apply pl-0;
  }
}
</style>
